import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { createShift, deleteShift, listShifts, updateShift } from "../api/shifts";
import { EmptyState, PageHeader } from "../components/ui";

const EMPTY_FORM = { shift_no: "", start_time: "", end_time: "", breaks: [] };

function Shifts() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");

  async function load() {
    try {
      setLoadError("");
      setItems(await listShifts());
    } catch (err) {
      setLoadError(err?.response?.data?.error?.message || "Vardiyalar yüklenemedi.");
      setItems([]);
    }
  }

  useEffect(() => { load(); }, []);

  function openEditor(item = null) {
    setSelected(item);
    setForm(item
      ? {
          shift_no: String(item.shift_no || ""),
          start_time: String(item.start_time || ""),
          end_time: String(item.end_time || ""),
          breaks: (item.breaks || []).map((breakItem) => ({
            break_type: String(breakItem.break_type || "Yemek Molası"),
            start_time: String(breakItem.start_time || ""),
            end_time: String(breakItem.end_time || ""),
          })),
        }
      : { ...EMPTY_FORM, breaks: [] });
    setError("");
    setOpen(true);
  }

  async function save() {
    if (!form.shift_no || !form.start_time || !form.end_time) {
      setError("Vardiya numarası, başlangıç ve bitiş saatleri zorunludur.");
      return;
    }
    if (form.breaks.some((breakItem) => !breakItem.start_time || !breakItem.end_time)) {
      setError("Eklenen molaların başlangıç ve bitiş saatleri zorunludur.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        shift_no: Number(form.shift_no),
        start_time: form.start_time,
        end_time: form.end_time,
        breaks: form.breaks,
      };
      if (selected?.id) await updateShift(selected.id, payload);
      else await createShift(payload);
      setOpen(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Vardiya kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(item) {
    if (!window.confirm(`${item.shift_no}. vardiya silinsin mi?`)) return;
    try {
      await deleteShift(item.id);
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.error?.message || "Vardiya silinemedi.");
    }
  }

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Vardiyalar"
        subtitle="Çalışma vardiyalarının başlangıç ve bitiş saatlerini yönetin."
        actions={<Button variant="contained" onClick={() => openEditor()}>+ Yeni Vardiya</Button>}
      />

      {loadError ? <Alert severity="error">{loadError}</Alert> : null}
      {!items.length && !loadError ? (
        <EmptyState description="Henüz vardiya tanımı yok." actionLabel="+ Yeni Vardiya" onAction={() => openEditor()} />
      ) : items.length ? (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Vardiya</TableCell>
                <TableCell>Başlangıç</TableCell>
                <TableCell>Bitiş</TableCell>
                <TableCell align="right">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell><Typography fontWeight={800}>{item.shift_no}. Vardiya</Typography></TableCell>
                  <TableCell>{item.start_time}</TableCell>
                  <TableCell>{item.end_time}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="outlined" onClick={() => openEditor(item)}>Düzenle</Button>
                      <Button size="small" color="error" variant="outlined" onClick={() => remove(item)}>Sil</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : null}

      <Dialog open={open} onClose={saving ? undefined : () => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{selected ? "Vardiya Düzenle" : "Yeni Vardiya"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField
              label="Vardiya"
              type="number"
              value={form.shift_no}
              onChange={(e) => setForm((prev) => ({ ...prev, shift_no: e.target.value }))}
              inputProps={{ min: 1, step: 1 }}
              required
              disabled={saving}
              placeholder="Örn. 1"
            />
            <TextField
              label="Başlangıç"
              type="time"
              value={form.start_time}
              onChange={(e) => setForm((prev) => ({ ...prev, start_time: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              required
              disabled={saving}
            />
            <TextField
              label="Bitiş"
              type="time"
              value={form.end_time}
              onChange={(e) => setForm((prev) => ({ ...prev, end_time: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              required
              disabled={saving}
            />
            <Stack spacing={1} sx={{ pt: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle1" fontWeight={800}>Molalar</Typography>
                <Button
                  size="small"
                  onClick={() => setForm((prev) => ({
                    ...prev,
                    breaks: [...prev.breaks, { break_type: "Yemek Molası", start_time: "", end_time: "" }],
                  }))}
                  disabled={saving}
                >
                  + Mola ekle
                </Button>
              </Stack>
              {form.breaks.map((breakItem, index) => (
                <Stack key={`${index}-${breakItem.break_type}`} direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                  <TextField
                    label="Mola"
                    value="Yemek Molası"
                    size="small"
                    sx={{ flex: 1 }}
                    InputProps={{ readOnly: true }}
                    disabled={saving}
                  />
                  <TextField
                    label="Başlangıç"
                    type="time"
                    value={breakItem.start_time}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      breaks: prev.breaks.map((item, itemIndex) => itemIndex === index ? { ...item, start_time: e.target.value } : item),
                    }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    required
                    disabled={saving}
                  />
                  <TextField
                    label="Bitiş"
                    type="time"
                    value={breakItem.end_time}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      breaks: prev.breaks.map((item, itemIndex) => itemIndex === index ? { ...item, end_time: e.target.value } : item),
                    }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    required
                    disabled={saving}
                  />
                  <IconButton
                    color="error"
                    aria-label="Molayı sil"
                    onClick={() => setForm((prev) => ({ ...prev, breaks: prev.breaks.filter((_, itemIndex) => itemIndex !== index) }))}
                    disabled={saving}
                  >
                    ×
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="inherit" disabled={saving}>İptal</Button>
          <Button onClick={save} variant="contained" disabled={saving}>{saving ? "Kaydediliyor..." : "Kaydet"}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default Shifts;
